import React, { useMemo, useState } from 'react';
import DropdownStateManager, {
  DropdownStateManagerProps,
} from '../DropdownStateManager';
import { PopupContext } from '../../popups/PopupContext';
import noop from 'lodash/noop';
import {
  PopperDropdownContext,
  PopperDropdownContextProps,
} from './PopperDropdownContext';

export interface Props extends DropdownStateManagerProps {
  /**
   * If a dropdown is located inside of a popup, we need to tell it so that it manages z-indexes correctly.
   *
   * This will render the content of a Dropdown component inside a PopupContext.Provider with isInPopup = true.
   *
   * No context is added if the `isInPopup` prop on this component is false or undefined to allow for outside customization.
   */
  isInPopup?: boolean;
}

const PopperDropdownManager: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  isInPopup,
  ...dropdownProps
}) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  );
  const contextData: PopperDropdownContextProps = useMemo(
    () => ({
      setToggleRef: setReferenceElement,
      toggleElement: referenceElement,
    }),
    [referenceElement]
  );

  return (
    <DropdownStateManager {...dropdownProps}>
      <PopperDropdownContext.Provider value={contextData}>
        {/**
        Why not wrap this in a popup with isInPopup = false?

        To allow a user to provide their own popup context for a dropdown without worrying
        about this one overriding it.
       */}
        {isInPopup ? (
          <PopupContext.Provider
            value={{ isInPopup: true, setScrollableContentRef: noop }}
          >
            {children}
          </PopupContext.Provider>
        ) : (
          children
        )}
      </PopperDropdownContext.Provider>
    </DropdownStateManager>
  );
};
PopperDropdownManager.displayName = 'PopperDropdownManager';

export default PopperDropdownManager;
